import { useMemo } from "react";
import { observer } from "mobx-react-lite";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useIssuesStore } from "@/stores/pm/hooks/usePMStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { StatusBadge } from "@/domains/pm/components/shared";

interface ParentIssuePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Building scope — only issues in this building appear in the list. */
  buildingId: number;
  /** Id of the issue we're picking a parent FOR. Excluded from the
   *  candidate list and used to skip self-parent. May be `null` for
   *  the "create new issue → choose parent" flow before the new issue
   *  has been saved. */
  currentIssueId: number | null;
  /** Fired with the chosen parent's id. */
  onPicked: (parentId: number) => void;
}

/**
 * WHA-3389: modal for picking an existing issue to mark as the parent.
 *
 * Constraints (mirror the DB trigger from WHA-3331):
 *   - Same building
 *   - Parent must itself be top-level (parent_issue_id IS NULL) — the
 *     product enforces a single level of nesting.
 *   - Cannot self-parent.
 *
 * Source list is `issuesStore.issues`, which the page has already
 * loaded for the current building. No additional fetch.
 */
export const ParentIssuePickerDialog = observer(function ParentIssuePickerDialog({
  open,
  onOpenChange,
  buildingId,
  currentIssueId,
  onPicked,
}: ParentIssuePickerDialogProps) {
  const { t } = useLanguage();
  const issuesStore = useIssuesStore();

  const candidates = useMemo(() => {
    return issuesStore.issues.filter(
      (i) =>
        i.building_id === buildingId &&
        i.deleted_at == null &&
        i.parent_issue_id == null &&
        i.id !== currentIssueId
    );
  }, [issuesStore.issues, buildingId, currentIssueId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg overflow-hidden p-0">
        <DialogHeader className="px-4 pb-2 pt-4">
          <DialogTitle className="text-base">{t.pm.issues.detail.pickParent}</DialogTitle>
        </DialogHeader>
        <Command shouldFilter>
          <CommandInput placeholder={t.pm.issues.detail.searchIssues} />
          <CommandList>
            <CommandEmpty>{t.pm.issues.empty}</CommandEmpty>
            <CommandGroup>
              {candidates.map((issue) => (
                <CommandItem
                  key={issue.id}
                  // cmdk filters by the value string — include both the
                  // issue_number and title so either matches.
                  value={`#${issue.issue_number} ${issue.title}`}
                  onSelect={() => {
                    onPicked(issue.id);
                    onOpenChange(false);
                  }}
                  className="flex items-center gap-3 px-3 py-2"
                >
                  <span className="text-[11px] font-medium text-muted-foreground">
                    #{issue.issue_number}
                  </span>
                  <span className="flex-1 truncate text-sm">{issue.title}</span>
                  <StatusBadge status={issue.status} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
});
